/** TanStack Query hooks for fault analysis. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getCombinedFaultStudy,
  getCoordinationFindings,
  getDeviceRatingCoverage,
  getLatestFaultStudy,
  getProtectionTree,
  getTccPairCurves,
  persistFaultStudy,
  putCoordinationDisposition,
} from '../api/faultAnalysis.ts'
import type { CombinedFaultStudyResult } from '../types/faultAnalysis.ts'

/**
 * Shared compute-once fault study. The study, device-duty, and arc-flash panels
 * all call this with the SAME queryKey, so react-query dedups them into ONE
 * network request; each panel passes a `select` to read its own slice.
 */
export function useFaultStudyCombined<T = CombinedFaultStudyResult>(
  feederId: number | null,
  select?: (r: CombinedFaultStudyResult) => T,
) {
  return useQuery({
    queryKey: ['engineering', 'fault-study-combined', feederId],
    queryFn: () => getCombinedFaultStudy(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
    select,
  })
}

export function useProtectionTree(feederId: number | null) {
  return useQuery({
    queryKey: ['engineering', 'protection-tree', feederId],
    queryFn: () => getProtectionTree(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
  })
}

export function useDeviceRatingCoverage(feederId: number | null) {
  return useQuery({
    queryKey: ['engineering', 'device-rating-coverage', feederId],
    queryFn: () => getDeviceRatingCoverage(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
  })
}

/** The latest persisted fault-study snapshot for a feeder (the compare's
 *  PERSISTED side). Returns an empty-node snapshot when nothing is saved yet. */
export function useLatestFaultStudy(feederId: number | null) {
  return useQuery({
    queryKey: ['engineering', 'fault-study-latest', feederId],
    queryFn: () => getLatestFaultStudy(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
  })
}

/** Persist the current live study as the feeder's snapshot (replace-all).
 *  On success invalidates the latest-snapshot key so the compare view re-reads
 *  the freshly saved snapshot (no stale compare after persisting). */
export function usePersistFaultStudy(feederId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => persistFaultStudy(feederId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['engineering', 'fault-study-latest', feederId] })
    },
  })
}

export function useTccPairCurves(
  feederId: number | null, upstreamId: number | null, downstreamId: number | null,
) {
  return useQuery({
    queryKey: ['engineering', 'tcc-pair', feederId, upstreamId, downstreamId],
    queryFn: () => getTccPairCurves(feederId!, upstreamId!, downstreamId!),
    staleTime: 120_000,
    enabled: feederId !== null && upstreamId !== null && downstreamId !== null,
  })
}
export function useCoordinationFindings(feederId: number | null) {
  return useQuery({
    queryKey: ['engineering', 'coordination-findings', feederId],
    queryFn: () => getCoordinationFindings(feederId!),
    staleTime: 120_000,
    enabled: feederId !== null,
  })
}
export function useCoordinationDisposition(feederId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: putCoordinationDisposition,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['engineering', 'protection-tree', feederId] })
      qc.invalidateQueries({ queryKey: ['engineering', 'coordination-findings', feederId] })
    },
  })
}
